import SEOMeta, { BOOK_META } from '../../../components/SEOMeta';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Apple, Monitor, Bell, Check, Loader2, QrCode, Copy } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const BOOK_SLUG = 'cultural-fluency-without-compromise';

// Editable configuration for app store links
// Update these when TestFlight/App Store links are available
const APP_CONFIG = {
  testFlightLink: '', // e.g., 'https://testflight.apple.com/join/xxxxx'
  appStoreLink: '',   // e.g., 'https://apps.apple.com/app/idXXXXXXXXX'
};

const BookIOSPage = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [copiedLink, setCopiedLink] = useState(null);
  
  // Editable link states (can be updated via admin later)
  const [testFlightUrl, setTestFlightUrl] = useState(APP_CONFIG.testFlightLink);
  const [appStoreUrl, setAppStoreUrl] = useState(APP_CONFIG.appStoreLink);

  const copyToClipboard = (url, label) => {
    navigator.clipboard.writeText(url);
    setCopiedLink(label);
    setTimeout(() => setCopiedLink(null), 2000);
  };

  const handleNotifyMe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    // Simulate API call - replace with actual backend call when ready
    await new Promise(resolve => setTimeout(resolve, 1000));
    setSubmitted(true);
    setIsSubmitting(false);
  };

  const isAppAvailable = testFlightUrl || appStoreUrl;

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      <SEOMeta 
        url="/books/cultural-fluency-without-compromise/ios"
        title="iPhone & iPad — Cultural Fluency Without Compromise"
        description="Read Cultural Fluency Without Compromise on your iPhone or iPad with the native iOS app."
        image={BOOK_META.image}
        imageAlt={BOOK_META.imageAlt}
      />
      {/* Header */}
      <header className="bg-gradient-to-br from-[#2c3e50] to-[#1a252f] text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Apple className="w-16 h-16 mx-auto mb-4 text-[#d4af37]" />
          <h1 className="text-3xl md:text-4xl font-bold mb-4 font-display">iPhone & iPad</h1>
          <p className="text-lg text-white/80 max-w-xl mx-auto">
            Read &ldquo;Cultural Fluency Without Compromise&rdquo; on your iOS devices
          </p>
        </div>
      </header>

      {/* Status Section */}
      <section className="py-16 px-4">
        <div className="max-w-2xl mx-auto">
          {isAppAvailable ? (
            /* App Available */
            <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-[#2c3e50] mb-4">App Available!</h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-8">
                {appStoreUrl && (
                  <div className="bg-[#faf8f5] rounded-xl p-6 text-center">
                    <div className="bg-white p-4 rounded-lg inline-block mb-4 shadow-sm">
                      <QRCodeSVG value={appStoreUrl} size={120} level="M" fgColor="#2c3e50" />
                    </div>
                    <h3 className="font-semibold text-[#2c3e50] mb-2">Scan for App Store</h3>
                    <a
                      href={appStoreUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-black text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-800 transition-colors"
                    >
                      <Apple size={16} />
                      Download
                    </a>
                    <button
                      onClick={() => copyToClipboard(appStoreUrl, 'appstore')}
                      className="mt-2 flex items-center justify-center gap-1 text-xs text-[#d4af37] mx-auto"
                    >
                      {copiedLink === 'appstore' ? <Check size={12} /> : <Copy size={12} />}
                      {copiedLink === 'appstore' ? 'Copied!' : 'Copy link'}
                    </button>
                  </div>
                )}
                
                {testFlightUrl && (
                  <div className="bg-[#faf8f5] rounded-xl p-6 text-center">
                    <div className="bg-white p-4 rounded-lg inline-block mb-4 shadow-sm">
                      <QRCodeSVG value={testFlightUrl} size={120} level="M" fgColor="#007AFF" />
                    </div>
                    <h3 className="font-semibold text-[#2c3e50] mb-2">Scan for TestFlight</h3>
                    <a
                      href={testFlightUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-blue-500 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-600 transition-colors"
                    >
                      <Apple size={16} />
                      Join Beta
                    </a>
                    <button
                      onClick={() => copyToClipboard(testFlightUrl, 'testflight')}
                      className="mt-2 flex items-center justify-center gap-1 text-xs text-[#d4af37] mx-auto"
                    >
                      {copiedLink === 'testflight' ? <Check size={12} /> : <Copy size={12} />}
                      {copiedLink === 'testflight' ? 'Copied!' : 'Copy link'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Coming Soon */
            <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
              <div className="w-20 h-20 bg-[#d4af37]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Apple className="w-10 h-10 text-[#d4af37]" />
              </div>
              <span className="inline-block bg-[#2c3e50]/10 text-[#2c3e50] px-4 py-1 rounded-full text-sm font-medium mb-4">
                Coming Soon
              </span>
              <h2 className="text-2xl font-bold text-[#2c3e50] mb-4">iOS App in Development</h2>
              <p className="text-[#5a6c7d] mb-8 max-w-md mx-auto">
                We&apos;re building a beautiful native iOS app for iPhone and iPad. 
                Sign up to be notified when it&apos;s ready.
              </p>
              
              {/* Placeholder for future links */}
              <div className="bg-[#faf8f5] rounded-xl p-6 mb-8">
                <QrCode className="w-8 h-8 mx-auto mb-2 text-[#5a6c7d]/50" />
                <p className="text-sm text-[#5a6c7d]">QR codes will appear here when the app is available</p>
              </div>
              
              {!submitted ? (
                <form onSubmit={handleNotifyMe} className="max-w-sm mx-auto">
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="flex-1 px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d4af37] focus:border-transparent"
                      required
                      data-testid="notify-email-input"
                    />
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="bg-[#d4af37] text-[#2c3e50] px-6 py-3 rounded-lg font-semibold hover:bg-[#e5c349] transition-colors disabled:opacity-50 flex items-center gap-2"
                      data-testid="notify-submit-btn"
                    >
                      {isSubmitting ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <>
                          <Bell size={18} />
                          Notify Me
                        </>
                      )}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="bg-green-50 text-green-700 px-6 py-4 rounded-lg inline-flex items-center gap-2">
                  <Check size={20} />
                  <span>We&apos;ll notify you when the iOS app is ready!</span>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Features Preview */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-[#2c3e50] text-center mb-12 font-display">
            What to Expect
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: 'Offline Reading', desc: 'Download chapters for reading without internet' },
              { title: 'iCloud Sync', desc: 'Your progress syncs across all Apple devices' },
              { title: 'Beautiful Typography', desc: 'San Francisco font with optimal line spacing' },
              { title: 'Dark Mode', desc: 'Full support for iOS dark mode' },
              { title: 'iPad Multitasking', desc: 'Split View and Slide Over support' },
              { title: 'Apple Silicon Mac', desc: 'Run the iPad app on your Mac' },
            ].map((feature, idx) => (
              <div key={idx} className="text-center">
                <h3 className="font-semibold text-[#2c3e50] mb-2">{feature.title}</h3>
                <p className="text-sm text-[#5a6c7d]">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Web Reader CTA */}
      <section className="py-16 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <Monitor className="w-12 h-12 mx-auto mb-4 text-[#d4af37]" />
          <h2 className="text-2xl font-bold text-[#2c3e50] mb-4 font-display">
            Can&apos;t Wait?
          </h2>
          <p className="text-[#5a6c7d] mb-6">
            Start reading now in your browser. The Web Reader works great on iPhone and iPad Safari.
          </p>
          <Link
            to={`/books/${BOOK_SLUG}/read`}
            className="inline-flex items-center gap-2 bg-[#d4af37] text-[#2c3e50] px-6 py-3 rounded-lg font-semibold hover:bg-[#e5c349] transition-colors"
            data-testid="use-web-reader-btn"
          >
            <Monitor size={20} />
            Use Web Reader Now
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2c3e50] text-white py-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Link 
            to={`/books/${BOOK_SLUG}/apps`}
            className="text-[#d4af37] hover:underline"
          >
            ← Back to Apps
          </Link>
          <p className="text-sm text-white/50 mt-4">
            © {new Date().getFullYear()} Third Culture Leadership. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default BookIOSPage;
